import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { ArrowLeft } from "@phosphor-icons/react";

const mdComponents = {
  h1: ({ children }) => <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-12 mb-4 text-zinc-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{children}</h1>,
  h2: ({ children }) => <h2 className="text-2xl font-bold tracking-tight mt-12 mb-4 text-zinc-950" style={{ fontFamily: "'Outfit', sans-serif" }}>{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold tracking-tight mt-8 mb-3 text-zinc-800">{children}</h3>,
  p: ({ children }) => <p className="text-base text-zinc-700 leading-relaxed mb-5">{children}</p>,
  a: ({ href, children }) => <a href={href} className="text-[hsl(var(--appistry-signal))] hover:underline" target={href?.startsWith("http") ? "_blank" : undefined} rel={href?.startsWith("http") ? "noreferrer" : undefined}>{children}</a>,
  strong: ({ children }) => <strong className="font-semibold text-zinc-950">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-5 space-y-2 text-zinc-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-5 space-y-2 text-zinc-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-6 border border-zinc-200">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-zinc-100">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-zinc-800 border-b border-zinc-200">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-zinc-700 border-b border-zinc-100 align-top">{children}</td>,
  hr: () => <hr className="my-10 border-zinc-200" />,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-zinc-300 pl-4 italic text-zinc-600 mb-5">{children}</blockquote>,
  code: ({ children }) => <code className="bg-zinc-100 px-1.5 py-0.5 text-[0.9em] font-mono text-zinc-800">{children}</code>,
};

export default function LegalPage({ slug: forcedSlug }) {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null);
    setError(null);
    api
      .get(`/legal/${slug}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => setError(e.response?.status === 404 ? "Document not found." : "Could not load this document."));
  }, [slug]);

  return (
    <div className="bg-zinc-50 min-h-screen" data-testid={`legal-page-${slug}`}>
      <div className="app-container py-3">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors">
          <ArrowLeft size={14} /> Home
        </Link>
      </div>
      <article className="app-container py-10 md:py-14 max-w-3xl">
        {error && (
          <div className="border border-zinc-200 bg-white p-8 text-center text-zinc-500 font-mono text-sm" data-testid="legal-error">
            {error}
          </div>
        )}
        {!error && !doc && (
          <div className="font-mono text-sm text-zinc-500">Loading…</div>
        )}
        {doc && (
          <>
            <p className="overline mb-3">Legal</p>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-none mb-3 text-zinc-950" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="legal-title">
              {doc.title}
            </h1>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-10" data-testid="legal-meta">
              v{doc.version} · Updated {doc.updated_at?.slice(0, 10)}
            </p>
            <div data-testid="legal-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{doc.body}</ReactMarkdown>
            </div>
          </>
        )}
      </article>
    </div>
  );
}
